import type { Collection, CollectionsConfig } from "./types";

export interface CollectionProgress {
  collectionId: string;
  completedLevels: number[];
  currentLevel: number;
  unlocked: boolean;
}

export interface GlobalProgress {
  collections: { [key: string]: CollectionProgress };
  totalLevelsCompleted: number;
  currentCollectionId: string;
  coins: number;
}

// Track if currently replaying (no coins awarded)
let isReplayMode = false;

export class CollectionManager {
  private static STORAGE_KEY = "jigsaw_collection_progress";
  private collections: Collection[] = [];
  private progress: GlobalProgress;

  constructor() {
    this.progress = this.loadProgress();
  }

  public loadCollections(config: CollectionsConfig): void {
    this.collections = config.collections;
    this.initializeProgress();
  }

  private initializeProgress(): void {
    // Initialize progress for any new collections
    for (const collection of this.collections) {
      if (!this.progress.collections[collection.id]) {
        this.progress.collections[collection.id] = {
          collectionId: collection.id,
          completedLevels: [],
          currentLevel: 1,
          unlocked: collection.unlocked, // Free collections start unlocked
        };
      }
    }

    // Set current collection to first unlocked if not set
    if (!this.progress.currentCollectionId) {
      const firstUnlocked = this.collections.find((c) =>
        this.isCollectionUnlocked(c.id),
      );
      if (firstUnlocked) {
        this.progress.currentCollectionId = firstUnlocked.id;
      }
    }

    this.saveProgress();
  }

  public getCollections(deviceFilter?: "desktop" | "mobile"): Collection[] {
    let filtered = this.collections;

    if (deviceFilter) {
      filtered = this.collections.filter(
        (c) => !c.device || c.device === "both" || c.device === deviceFilter,
      );
    }

    return filtered.map((c) => ({
      ...c,
      unlocked: this.isCollectionUnlocked(c.id),
    }));
  }

  public getCollection(collectionId: string): Collection | null {
    const collection = this.collections.find((c) => c.id === collectionId);
    if (collection) {
      return {
        ...collection,
        unlocked: this.isCollectionUnlocked(collectionId),
      };
    }
    return null;
  }

  public getCurrentCollection(): Collection | null {
    return this.getCollection(this.progress.currentCollectionId);
  }

  public setCurrentCollection(collectionId: string): boolean {
    if (this.isCollectionUnlocked(collectionId)) {
      this.progress.currentCollectionId = collectionId;
      this.saveProgress();
      return true;
    }
    return false;
  }

  public getCollectionProgress(
    collectionId: string,
  ): CollectionProgress | null {
    return this.progress.collections[collectionId] || null;
  }

  public completeLevel(
    collectionId: string,
    levelId: number,
    coinsEarned: number = 10,
  ): void {
    const progress = this.progress.collections[collectionId];
    if (progress && !progress.completedLevels.includes(levelId)) {
      progress.completedLevels.push(levelId);
      this.progress.totalLevelsCompleted++;

      // Award coins
      this.progress.coins += coinsEarned;

      // Update current level to next uncompleted
      const collection = this.getCollection(collectionId);
      if (collection) {
        const nextLevel = collection.levels.find(
          (l) => !progress.completedLevels.includes(l.id),
        );
        if (nextLevel) {
          progress.currentLevel = nextLevel.id;
        }
      }

      this.saveProgress();
    }
  }

  public getCollectionCompletion(collectionId: string): number {
    const progress = this.progress.collections[collectionId];
    const collection = this.getCollection(collectionId);

    if (!progress || !collection) return 0;

    return (progress.completedLevels.length / collection.levels.length) * 100;
  }

  public isCollectionUnlocked(collectionId: string): boolean {
    const collection = this.collections.find((c) => c.id === collectionId);
    if (!collection) return false;

    // Check if already unlocked in progress
    const progress = this.progress.collections[collectionId];
    if (progress?.unlocked) return true;

    // Free collections (no price) are unlocked by default
    return collection.unlocked;
  }

  public getTotalProgress(): {
    totalLevels: number;
    completedLevels: number;
    percentage: number;
  } {
    let totalLevels = 0;
    let completedLevels = 0;

    for (const collection of this.collections) {
      if (this.isCollectionUnlocked(collection.id)) {
        totalLevels += collection.levels.length;
        const progress = this.progress.collections[collection.id];
        if (progress) {
          completedLevels += progress.completedLevels.length;
        }
      }
    }

    return {
      totalLevels,
      completedLevels,
      percentage: totalLevels > 0 ? (completedLevels / totalLevels) * 100 : 0,
    };
  }

  private loadProgress(): GlobalProgress {
    const saved = localStorage.getItem(CollectionManager.STORAGE_KEY);
    if (saved) {
      const progress = JSON.parse(saved);
      // Ensure coins field exists (for backwards compatibility)
      if (progress.coins === undefined) {
        progress.coins = 0;
      }
      return progress;
    }

    return {
      collections: {},
      totalLevelsCompleted: 0,
      currentCollectionId: "",
      coins: 0,
    };
  }

  private saveProgress(): void {
    localStorage.setItem(
      CollectionManager.STORAGE_KEY,
      JSON.stringify(this.progress),
    );
  }

  public resetProgress(): void {
    localStorage.removeItem(CollectionManager.STORAGE_KEY);
    this.progress = {
      collections: {},
      totalLevelsCompleted: 0,
      currentCollectionId: "",
      coins: 0,
    };
    this.initializeProgress();
  }

  // Coin system methods
  public getCoins(): number {
    return this.progress.coins;
  }

  public addCoins(amount: number): void {
    this.progress.coins += amount;
    this.saveProgress();
  }

  public canAffordCollection(collectionId: string): boolean {
    const collection = this.collections.find((c) => c.id === collectionId);
    if (!collection || !collection.price) return false;
    return this.progress.coins >= collection.price;
  }

  public purchaseCollection(collectionId: string): boolean {
    const collection = this.collections.find((c) => c.id === collectionId);
    if (!collection || !collection.price) return false;

    // Check if already unlocked
    if (this.isCollectionUnlocked(collectionId)) return false;

    // Check if can afford
    if (this.progress.coins < collection.price) return false;

    // Purchase
    this.progress.coins -= collection.price;
    const progress = this.progress.collections[collectionId];
    if (progress) {
      progress.unlocked = true;
    }
    this.saveProgress();
    return true;
  }

  // Replay methods
  public startReplay(collectionId: string): boolean {
    const progress = this.progress.collections[collectionId];
    if (!progress) return false;

    // Set replay mode and reset to level 1
    isReplayMode = true;
    progress.currentLevel = 1;
    this.progress.currentCollectionId = collectionId;
    this.saveProgress();
    return true;
  }

  public isReplaying(): boolean {
    return isReplayMode;
  }

  public endReplay(): void {
    isReplayMode = false;
  }
}
